import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { MiDetailFields, MiModal } from "@miplatform/components";
import { BUTTON_VARIANTS } from "@miplatform/constants";
import { ISeriesGroupResponse } from "@miplatform/types";
import { zodResolver } from "@hookform/resolvers/zod";
import { useAddSeriesGroupModalViewModel } from "./useAddSeriesGroupModalViewModel";

const schema = z.object({
  name: z.string().min(1, "Series Group Name is required"),
  accountId: z.string().uuid(),
});

interface AddSeriesGroupModalProps {
  open: boolean;
  closeModal: () => void;
  selectedSeriesGroup: ISeriesGroupResponse | null;
  setSelectedSeriesGroup: (seriesGroup: ISeriesGroupResponse | null) => void;
  setRefresh: (updater: (prev: boolean) => boolean) => void;
  accountId: string;
}

export function AddSeriesGroupModal({
  open,
  closeModal,
  selectedSeriesGroup,
  setSelectedSeriesGroup,
  setRefresh,
  accountId,
}: AddSeriesGroupModalProps) {
  const {
    formState: { isValid, isDirty },
    control,
    reset,
    getValues,
  } = useForm({
    mode: "onChange",
    defaultValues: {
      name: "",
      accountId,
    },
    resolver: zodResolver(schema),
  });

  useEffect(() => {
    if (selectedSeriesGroup?.id) {
      reset(selectedSeriesGroup);
    } else {
      reset({ name: "", accountId });
    }
  }, [selectedSeriesGroup, accountId]);

  const handleCloseModal = () => {
    setSelectedSeriesGroup(null);
    closeModal();
    reset();
  };

  const { isLoading, handleCreateSeriesGroup, handleUpdateSeriesGroup } =
    useAddSeriesGroupModalViewModel({ setRefresh, handleCloseModal });

  const seriesGroupFields = [
    {
      label: "Series Group Name",
      name: "name",
      formControl: control,
      required: true,
      inputProps: { maxLength: 128 },
    },
  ];

  const handleSave = () => {
    handleCreateSeriesGroup(getValues());
  };

  const handleUpdate = () => {
    handleUpdateSeriesGroup(getValues());
  };

  return (
    <MiModal
      open={open}
      closeModal={handleCloseModal}
      loading={isLoading}
      title={`${selectedSeriesGroup?.id ? "Edit" : "Add"} Series Group`}
      description={`${
        selectedSeriesGroup?.id ? "Edit" : "Enter"
      } series group details below.`}
      actions={[
        {
          name: "Cancel",
          onClick: handleCloseModal,
        },
        {
          name: selectedSeriesGroup?.id
            ? "Update Series Group"
            : "Save Series Group",
          variant: BUTTON_VARIANTS.CONTAINED,
          onClick: selectedSeriesGroup?.id ? handleUpdate : handleSave,
          disabled: !isValid || !isDirty,
        },
      ]}
    >
      <MiDetailFields detailFields={seriesGroupFields} />
    </MiModal>
  );
}
